import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { erpRegistriesService } from '../../api/erpRegistriesService';
import type { ErpRegistry } from '../../api/erpRegistriesService';
import {
  ERP_ENVIRONMENT_LABELS,
  getErpEnvironmentBadgeVariant,
} from '../../configs/erpEnvironment';

const AUTH_TYPE_LABELS: Record<ErpRegistry['auth_type'], string> = {
  api_key: 'API Key',
  oauth2_client: 'OAuth2',
  basic: 'Basic',
  bearer: 'Bearer',
  none: 'None',
  login_bearer: 'Login Bearer',
};

export function RegistriesListPage() {
  const navigate = useNavigate();

  const listPage = useAdminListPage<ErpRegistry, string>({
    queryKey: ['admin', 'erp', 'registries'],
    queryFn: () => erpRegistriesService.list(),
    deleteFn: (slug) => erpRegistriesService.remove(slug),
    getDeleteId: (registry) => registry.slug,
  });

  return (
    <AdminListPage<ErpRegistry>
      title="ERP Registries"
      subtitle="Manage your ERP integration registries"
      loadingLabel="Loading registries..."
      errorTitle="Failed to load registries"
      errorFallback="An unexpected error occurred while loading registries."
      showTitleOnError
      emptyTitle="No registries found"
      emptyDescription="Get started by creating your first ERP registry."
      createLabel="Create Registry"
      onCreateClick={() => navigate('/admin/erp/registries/create')}
      items={listPage.items}
      getRowKey={(registry) => registry.id}
      onRowClick={(registry) => navigate(`/admin/erp/registries/${registry.slug}`)}
      isLoading={listPage.isLoading}
      error={listPage.error}
      deleteModalTitle="Delete Registry"
      deleteTarget={listPage.deleteTarget}
      getDeleteTargetName={(registry) => registry.name}
      deleteNoun="registry"
      onDeleteCancel={listPage.cancelDelete}
      onDeleteConfirm={listPage.confirmDelete}
      isDeleting={listPage.isDeleting}
      isDeleteError={listPage.isDeleteError}
      deleteError={listPage.deleteError}
      renderHead={() => (
        <>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell>Slug</Table.HeaderCell>
          <Table.HeaderCell>Base URL</Table.HeaderCell>
          <Table.HeaderCell align="center">Environment</Table.HeaderCell>
          <Table.HeaderCell align="center">Auth Type</Table.HeaderCell>
          <Table.HeaderCell align="center">Endpoints</Table.HeaderCell>
          <Table.HeaderCell align="center">Status</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </>
      )}
      renderRow={(registry) => (
        <>
          <Table.Cell>
            <Text weight="medium">{registry.name}</Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" color="muted">
              {registry.slug}
            </Text>
          </Table.Cell>
          <Table.Cell>
            <Text size="sm" color="muted" truncate>
              {registry.base_url}
            </Text>
          </Table.Cell>
          <Table.Cell align="center">
            {registry.environment ? (
              <Badge
                variant={getErpEnvironmentBadgeVariant(registry.environment)}
                size="sm"
              >
                {ERP_ENVIRONMENT_LABELS[registry.environment]}
              </Badge>
            ) : (
              <Text size="sm" color="muted">
                —
              </Text>
            )}
          </Table.Cell>
          <Table.Cell align="center">
            <Badge variant="info" size="sm">
              {AUTH_TYPE_LABELS[registry.auth_type]}
            </Badge>
          </Table.Cell>
          <Table.Cell align="center">
            <Text size="sm">{registry.endpoints_count}</Text>
          </Table.Cell>
          <Table.Cell align="center">
            <Badge
              variant={registry.is_active ? 'success' : 'default'}
              size="sm"
            >
              {registry.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </Table.Cell>
          <Table.Cell align="right">
            <HStack gap={1} justify="end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${registry.name}`}
                leftIcon={<Icon icon={Pencil} size="sm" />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/erp/registries/${registry.slug}/edit`);
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete ${registry.name}`}
                leftIcon={<Icon icon={Trash2} size="sm" color="error" />}
                onClick={(e) => {
                  e.stopPropagation();
                  listPage.setDeleteTarget(registry);
                }}
              />
            </HStack>
          </Table.Cell>
        </>
      )}
    />
  );
}
